import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { logger } from "@gui/infrastructure/pass-through/logger";
import { sentryFeedback } from "@gui/infrastructure/pass-through/sentryFeedback";
import { Notifications } from "@gui/infrastructure/pass-through/notifications";
import { env } from "@gui/infrastructure/pass-through/env";
import { isAbortRejection, isDragInvariant, isInterceptorHandled } from "@gui/infrastructure/pass-through/sentryErrors";
import { makeTracesSampler, profilesSampleRate } from "@gui/infrastructure/pass-through/sentrySampling";
import { replayNetworkOptions } from "@gui/infrastructure/pass-through/sentryScope";

/**
 * Initialize Sentry, configure tags, install the global unhandled-rejection handler,
 * and link Sentry to the logger. Call once at app boot before any UI renders.
 */
export function initSentry(): void {
	const isDev = env.environmentName === "development";
	const isLocalhostInUrl = window.location.hostname.search("localhost") !== -1;

	Sentry.init({
		dsn: env.sentryDsn,
		environment: env.environmentName,
		beforeSend(event, hint) {
			// A cancelled request is not a defect — see `isAbortRejection`. Dropped here rather
			// than in `ignoreErrors` so the decision is keyed on the exception's name instead of
			// on a browser-worded message.
			if (isAbortRejection(hint?.originalException)) return null;
			// The axios interceptor already ruled on this one — including "don't report it",
			// for the session-expiry 401 (see `isInterceptorHandled`). Only automatic captures
			// are dropped: the interceptor's own deliberate reports for a network failure, 403
			// or 5xx come through `captureException` with `handled: true` and must survive,
			// whether or not the flag is set on the error by the time this runs.
			if (!hint?.mechanism?.handled && isInterceptorHandled(hint?.originalException)) return null;
			// A drag-and-drop auto-scroll frame that outlived its drag — see `isDragInvariant`.
			// `@hello-pangea/dnd` already preventDefaults this one on the window `error` event;
			// Sentry's requestAnimationFrame wrapper just gets to it first. Only the automatic
			// capture is dropped, so a dnd failure that reaches an error boundary still reports.
			if (!hint?.mechanism?.handled && isDragInvariant(hint?.originalException)) return null;
			// An unhandled crash opens the feedback widget (screenshots, replay) linked to this
			// event, in place of the legacy `showReportDialog`. Boundary-caught crashes are
			// `handled` and offer the same form from their fallback's "Report problem" button.
			if (event.exception && event.event_id && !hint?.mechanism?.handled) {
				void sentryFeedback.openCrashForm(event.event_id);
			}
			return event;
		},
		enableLogs: true,
		integrations: [
			// Browser tracing enables INP tracking by default in @sentry/react v10
			// (no enableInp/interactionsSampleRate option needed). Per-interaction INP attribution
			// is reported separately via initPerformanceMonitoring (web-vitals/attribution).
			// The React Router v6 variant names pageload/navigation transactions by route pattern
			// (`/jobs/:jobId`) instead of the concrete URL, so performance data for one page is not
			// split per job/robot. It only sees routes rendered through `SentryRoutes` (App.tsx).
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.browserProfilingIntegration(),
			// Records request/response bodies for Portal API calls (credential endpoints denied, sensitive
			// keys masked) so a replay of a failed request shows what was sent and answered.
			Sentry.replayIntegration(replayNetworkOptions(env.rogoApiUrl)),
			Sentry.feedbackIntegration({
				colorScheme: "system",
				enableScreenshot: true,
				showBranding: false,
				autoInject: false,
			}),
		],
		// A fifth of production page loads (all of job submission), every one elsewhere.
		tracesSampler: makeTracesSampler(env.isProduction),
		tracePropagationTargets: [
			// "localhost",
			// /^https:\/\/.*\.awsapprunner\.com/,
			env.rogoApiUrl,
		],
		profilesSampleRate: profilesSampleRate(env.isProduction), // share of the traced page loads
		replaysSessionSampleRate: isDev ? 1.0 : 0.1, // capture replays on 10% of sessions
		replaysOnErrorSampleRate: 1.0, // capture replays on 100% of errors
		enabled: (!isDev && !isLocalhostInUrl) || env.enableSentry,
	});

	Sentry.setTag("hostname", window.location.hostname);

	// Global handler for unhandled promise rejections.
	// Sentry already captures these; this adds a user-facing toast for promises that slip through without a catch.
	window.addEventListener("unhandledrejection", (event) => {
		if (isInterceptorHandled(event.reason)) return;
		// MapLibre aborts a tile's fetch on every pan; that is not something to tell the
		// operator about. Same predicate `beforeSend` uses, so the toast and the Sentry issue
		// appear and disappear together.
		if (isAbortRejection(event.reason)) return;
		Notifications.error("An unexpected error occurred.");
	});

	// @ts-expect-error @sentry/react's namespace vs shared-ts's aws-serverless-flavored Sentry type:
	// duplicate @sentry/core instances make Scope nominally incompatible (protected member) even
	// though the runtime surface Logger uses is identical. Fix belongs in @rogoag/shared-ts (type
	// `sentry` against a minimal structural interface).
	logger.sentry = Sentry;
	logger.newSession();
}
